import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { getDashboardDesignValues, setUserTimeZone, type DashboardDesignValues } from '../../../services/user.service';
import { Modal } from '../../../components/global/Modal';

interface Props {
  currentUser: any;
  showBanner: boolean;
  onCloseBanner: () => void;
}

export function Header({ currentUser, showBanner, onCloseBanner }: Props) {
  const [designValues, setDesignValues] = useState<DashboardDesignValues | null>(null);
  const DEFAULT_BANNER_URL = 'https://image.beholos.com/fit-in/1300x370/filters:quality(80)/img/fitness-pass-v2/global/profile-banner-v2.webp';
  const [showTzModal, setShowTzModal] = useState(false);
  const guessedTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return undefined;
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const values = await getDashboardDesignValues();
        if (values) setDesignValues(values);
      } catch (e) {
        // ignore errors and keep defaults
      }
    })();
  }, []);

  // Timezone detection/update
  useEffect(() => {
    if (!currentUser) return;
    const existing = (currentUser as any)?.timeZone || (currentUser as any)?.timezone;
    if (!guessedTimeZone) return;

    const init = async () => {
      // If no timezone stored, set it silently
      if (!existing) {
        await setUserTimeZone(guessedTimeZone);
        return;
      }
      // If different, show modal to confirm update
      if (existing && existing !== guessedTimeZone) {
        setShowTzModal(true);
      }
    };
    init();
    // we only want to run this on mount/when currentUser changes
  }, [currentUser, guessedTimeZone]);
  // Debug BMI value and user payload
  // eslint-disable-next-line no-console
  console.log('HomeHeader BMI debug', {
    rawBMI: currentUser?.BMI,
    typeofBMI: typeof currentUser?.BMI,
    userId: currentUser?.id || currentUser?._id,
    firstname: currentUser?.firstname,
  });
  return (
    <section id="header-section" className="relative">
      {showBanner && (
        <div className="w-full bg-holos text-black px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex-1 text-center">
              <span className="text-black uppercase text-sm font-medium">
                ¿TIENES DUDAS? ESCRIBENOS AL CHAT DE LA WEB O A NUESTRO{' '}
                <a
                  href="https://bit.ly/2QxXL5G"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WHATSAPP
                </a>
              </span>
            </div>
            <button
              onClick={onCloseBanner}
              className="ml-4 text-white hover:text-gray-300 transition-colors cursor-pointer"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div
        className="main-app-header-banner bg-cover bg-center relative overflow-hidden"
        style={{ backgroundImage: `url('${designValues?.dashboardBannerImage || DEFAULT_BANNER_URL}')` }}
      >
        <div className="header-content pl-0 pl-md-4 pt-3 pt-md-5 relative z-10">
          <div className="absolute ">
            <div className="flex-grow">
              <div className="flex font-medium text-2xl font-black font-juana pt-10 pl-10" style={{ color: designValues?.dashboardBannerTextColor }}>
                Hey,
                <span className="ml-1 mb-2 font-black" style={{ color: designValues?.dashboardBannerTextColor }}>{currentUser.firstname}</span>
              </div>
              <div className="dashboard-caption text-3xl font-black font-juana pl-10" style={{ color: designValues?.dashboardBannerTextColor }}>
                ¡Atrévete a todo
                <span className="font-black"> y descubre <br />lo que más te gusta!</span>
              </div>
            </div>
          </div>
        </div>
        <div className="h-48 md:h-110"></div>
      </div>

      {/* Timezone confirm modal */}
      <Modal isOpen={showTzModal} onClose={() => setShowTzModal(false)}>
        <div className="p-6 text-center">
          <div className="text-lg font-medium mb-6">¿Cambiar mi zona horaria a {guessedTimeZone}?</div>
          <button
            onClick={async () => {
              if (!guessedTimeZone) return;
              await setUserTimeZone(guessedTimeZone);
              setShowTzModal(false);
            }}
            className="w-full py-3 rounded-xl font-semibold text-black cursor-pointer"
            style={{ backgroundColor: '#A3BDF5' }}
          >
            SÍ, ACTUALIZAR AHORA.
          </button>
        </div>
      </Modal>

      <div className="flex justify-between items-stretch w-full px-6 md:-mt-48 mt-0 relative z-20 gap-6">
        {currentUser?.plan ? (
          <div className="remainingPasses bg-white border border-black rounded-2xl p-0 hidden md:block shadow-sm self-start" style={{ height: '150px' }}>
            <div className="flex items-stretch px-2 h-full">
              <div className="flex-1 min-w-0 flex flex-col items-start text-left border-r border-black px-3 h-full">
                <div className="flex-grow h-full py-1 flex flex-col justify-center">
                  <div className="user-summary-icon mb-2">
                    <img
                      src="https://cache.beholos.com/img/fitness-pass-v2/global/plan-icon-black.svg"
                      alt="Plan icon"
                      className="w-7 h-7"
                    />
                  </div>
                  <div>
                    {currentUser.plan?.sharedPassPlan ? (
                      <div className="text-sm font-light">Pase de invitados</div>
                    ) : (
                      <div className="mt-1 text-xs leading-tight">{currentUser.plan?.name || 'Plan Básico'}</div>
                    )}
                    <div className="mt-1 text-xs leading-tight">
                      <Link
                        to="/mi-membresia"
                        className="font-black font-bold text-black border border-black px-2 py-1 my-1 inline-block rounded-lg text-xs transition-colors"
                        style={{ backgroundColor: '#A3BDF5' }}
                      >
                        Ver Plan
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0 flex flex-col items-start text-left border-r border-black px-3 h-full">
                <div className="flex-grow h-full py-1 flex flex-col justify-center">
                  <div className="user-summary-icon mb-2">
                    <img
                      src="https://cache.beholos.com/img/fitness-pass-v2/global/clock-icon-black-v2.svg"
                      alt="Clock icon"
                      className="w-7 h-7"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-light">{(currentUser.totalMinutesTrained || 0).toLocaleString()}</div>
                    <div className="mt-1 text-xs leading-tight">Minutos entrenados</div>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0 flex flex-col items-start text-left border-r border-black px-3 h-full">
                <div className="flex-grow h-full py-1 flex flex-col justify-center">
                  <div className="user-summary-icon mb-2">
                    <img
                      src="https://cache.beholos.com/img/fitness-pass-v2/global/flame-icon.svg"
                      alt="Flame icon"
                      className="w-7 h-7"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-light">{(currentUser.totalCaloriesBurned || 0).toLocaleString()}</div>
                    <div className="mt-1 text-xs leading-tight">Calorías quemadas</div>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0 flex flex-col items-start text-left px-3 h-full">
                <div className="flex-grow h-full py-1 flex flex-col justify-center">
                  <div className="user-summary-icon mb-3 overflow-hidden" style={{ maxWidth: '40px', height: '40px' }}>
                    <img
                      src="https://cache.beholos.com/img/fitness-pass-v2/global/bmi-icon-black.svg"
                      alt="BMI icon"
                      className="w-7 h-7"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-light">{currentUser.BMI || 0}</div>
                    <div className="mt-1 text-xs leading-tight">IMC (BMI)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="remainingPasses bg-white border border-black rounded-2xl hidden md:flex items-center justify-center text-center p-3 shadow-sm self-start overflow-hidden" style={{ height: '150px' }}>
          <div className="p-2">
            <div className="font-black text-base mb-1">Sugiere tu partner</div>
            <div className="font-black text-sm mb-3">favorito</div>
            <div>
              <a
                href="/operaciones/partners/sugiere-un-partner"
                className="font-black font-bold text-black border border-black py-1 px-3 my-1 inline-block rounded-lg text-xs transition-colors"
                style={{ backgroundColor: '#A3BDF5' }}
              >
                Ir al formulario
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile quick stats */}
      <div className="md:hidden px-4 mt-8">
        {currentUser?.plan ? (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-white border border-gray-300 rounded-2xl p-4 text-center shadow-sm">
              <img
                src="https://cache.beholos.com/img/fitness-pass-v2/global/plan-icon-black.svg"
                alt="Plan icon"
                className="w-10 h-10 mx-auto mb-3"
              />
              <div className="text-sm mt-1">{currentUser.plan?.name || 'Basic'}</div>
            </div>
            <div className="bg-white border border-gray-300 rounded-2xl p-4 text-center shadow-sm">
              <img
                src="https://cache.beholos.com/img/fitness-pass-v2/global/clock-icon-black-v2.svg"
                alt="Clock icon"
                className="w-10 h-10 mx-auto mb-3"
              />
              <div className="text-base font-light">{(currentUser.totalMinutesTrained || 0).toLocaleString()}</div>
              <div className="text-sm mt-1">Minutos entrenados</div>
            </div>
            <div className="bg-white border border-gray-300 rounded-2xl p-4 text-center shadow-sm">
              <img
                src="https://cache.beholos.com/img/fitness-pass-v2/global/flame-icon.svg"
                alt="Flame icon"
                className="w-10 h-10 mx-auto mb-3"
              />
              <div className="text-base font-light">{(currentUser.totalCaloriesBurned || 0).toLocaleString()}</div>
              <div className="text-sm mt-1">Calorías quemadas</div>
            </div>
            <div className="bg-white border border-gray-300 rounded-2xl p-4 text-center shadow-sm">
              <img
                src="https://cache.beholos.com/img/fitness-pass-v2/global/bmi-icon-black.svg"
                alt="BMI icon"
                className="w-10 h-10 mx-auto mb-3"
              />
              <div className="text-base font-light">{currentUser.BMI || 0}</div>
              <div className="text-sm mt-1">IMC (BMI)</div>
            </div>
          </div>
        ) : null}

        <div className="bg-white border border-gray-300 rounded-2xl p-6 text-center shadow-sm">
          <div className="font-black text-lg mb-1">Sugiere tu partner</div>
          <div className="font-black text-base mb-4">favorito</div>
          <a
            href="/operaciones/partners/sugiere-un-partner"
            className="font-black font-bold text-black border border-black py-2 px-4 my-2 inline-block rounded-lg text-sm transition-colors"
            style={{ backgroundColor: '#A3BDF5' }}
          >
            Ir al formulario
          </a>
        </div>
      </div>
    </section>
  );
}


